import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  FormControl,
  FormControlLabel,
  Switch,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Slider,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Snackbar
} from '@mui/material';
import {
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  RestoreFromTrash as ResetIcon,
  Schedule as ScheduleIcon,
  Groups as CapacityIcon,
  Notifications as NotificationIcon,
  Info as InfoIcon,
  AccessTime as TimeIcon,
  CheckCircle as CheckIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';

const SystemSettingsPage = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Form state
  const [formData, setFormData] = useState({
    booking: {
      cancellationDeadlineHours: 12,
      enableAdminOverride: true,
      refundQuotaOnDeletion: true
    },
    capacity: {
      trackRealTimeOccupancy: false,
      enableCapacityAlerts: true,
      capacityAlertThreshold: 0.9
    },
    notifications: {
      sendCancellationNotifications: true,
      sendCapacityAlerts: true
    },
    emailVerification: {
      enableEmailVerification: true,
      expiryHours: 24,
      enableRateLimiting: true,
      maxRequestsPerHour: 5,
      requireForRegistration: true,
      allowAdminOverride: true
    }
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/system-settings');
      
      if (response.data.success) {
        const settingsData = response.data.data;
        setSettings(settingsData);
        setFormData({
          booking: {
            cancellationDeadlineHours: settingsData.booking.cancellationDeadlineHours,
            enableAdminOverride: settingsData.booking.enableAdminOverride,
            refundQuotaOnDeletion: settingsData.booking.refundQuotaOnDeletion
          },
          capacity: {
            trackRealTimeOccupancy: settingsData.capacity.trackRealTimeOccupancy,
            enableCapacityAlerts: settingsData.capacity.enableCapacityAlerts,
            capacityAlertThreshold: settingsData.capacity.capacityAlertThreshold
          },
          notifications: {
            sendCancellationNotifications: settingsData.notifications.sendCancellationNotifications,
            sendCapacityAlerts: settingsData.notifications.sendCapacityAlerts
          },
          emailVerification: {
            enableEmailVerification: settingsData.emailVerification?.enableEmailVerification ?? true,
            expiryHours: settingsData.emailVerification?.expiryHours ?? 24,
            enableRateLimiting: settingsData.emailVerification?.enableRateLimiting ?? true,
            maxRequestsPerHour: settingsData.emailVerification?.maxRequestsPerHour ?? 5,
            requireForRegistration: settingsData.emailVerification?.requireForRegistration ?? true,
            allowAdminOverride: settingsData.emailVerification?.allowAdminOverride ?? true
          }
        });
        setError('');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || t('admin:settings.errors.fetchFailed');
      setError(errorMessage);
      console.error('Error fetching system settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const response = await api.put('/system-settings', formData);
      
      if (response.data.success) {
        setSettings(response.data.data);
        setSuccess(t('admin:settings.messages.updateSuccess'));
        setSnackbar({
          open: true,
          message: t('admin:settings.messages.saveSuccess'),
          severity: 'success'
        });
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || t('admin:settings.errors.updateFailed');
      setError(errorMessage);
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      console.error('Error updating system settings:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      setResetting(true);
      setError('');
      setSuccess('');

      const response = await api.post('/system-settings/reset');
      
      if (response.data.success) {
        const settingsData = response.data.data;
        setSettings(settingsData);
        setFormData({
          booking: {
            cancellationDeadlineHours: settingsData.booking.cancellationDeadlineHours,
            enableAdminOverride: settingsData.booking.enableAdminOverride,
            refundQuotaOnDeletion: settingsData.booking.refundQuotaOnDeletion
          },
          capacity: {
            trackRealTimeOccupancy: settingsData.capacity.trackRealTimeOccupancy,
            enableCapacityAlerts: settingsData.capacity.enableCapacityAlerts,
            capacityAlertThreshold: settingsData.capacity.capacityAlertThreshold
          },
          notifications: {
            sendCancellationNotifications: settingsData.notifications.sendCancellationNotifications,
            sendCapacityAlerts: settingsData.notifications.sendCapacityAlerts
          }
        });
        setSuccess(t('admin:settings.messages.resetSuccess'));
        setSnackbar({
          open: true,
          message: t('admin:settings.messages.resetToDefaults'),
          severity: 'info'
        });
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || t('admin:settings.errors.resetFailed');
      setError(errorMessage);
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      console.error('Error resetting system settings:', err);
    } finally {
      setResetting(false);
    }
  };

  const handleFormChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const formatTimeFromHours = (hours) => {
    if (hours === 0) return t('admin:settings.time.immediate');
    if (hours === 1) return t('admin:settings.time.oneHour');
    if (hours < 24) return t('admin:settings.time.hours', { count: hours });
    if (hours === 24) return t('admin:settings.time.oneDay');
    return t('admin:settings.time.days', { count: Math.floor(hours / 24) });
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          <SettingsIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
          {t('admin:settings.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('admin:settings.description')}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Booking Settings */}
        <Grid size={{xs:12 , md:6}}>
          <Card>
            <CardHeader
              avatar={<ScheduleIcon color="primary" />}
              title={t('admin:settings.booking.title')}
              subheader={t('admin:settings.booking.description')}
            />
            <CardContent>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('admin:settings.booking.cancellationDeadline')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('admin:settings.booking.cancellationDeadlineDescription')}
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={formData.booking.cancellationDeadlineHours}
                  onChange={(e) => handleFormChange('booking', 'cancellationDeadlineHours', parseInt(e.target.value) || 0)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">hours</InputAdornment>,
                  }}
                  inputProps={{ min: 0, max: 168 }}
                  helperText={`Users can delete bookings up to ${formatTimeFromHours(formData.booking.cancellationDeadlineHours)} before start time`}
                />
                <Slider
                  value={formData.booking.cancellationDeadlineHours}
                  onChange={(e, value) => handleFormChange('booking', 'cancellationDeadlineHours', value)}
                  min={0}
                  max={72}
                  step={1}
                  marks={[
                    { value: 0, label: '0h' },
                    { value: 1, label: '1h' },
                    { value: 12, label: '12h' },
                    { value: 24, label: '1d' },
                    { value: 72, label: '3d' }
                  ]}
                  sx={{ mt: 2 }}
                />
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.booking.enableAdminOverride}
                    onChange={(e) => handleFormChange('booking', 'enableAdminOverride', e.target.checked)}
                  />
                }
                label={t('admin:settings.booking.enableAdminOverride')}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('admin:settings.booking.adminOverrideDescription')}
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.booking.refundQuotaOnDeletion}
                    onChange={(e) => handleFormChange('booking', 'refundQuotaOnDeletion', e.target.checked)}
                  />
                }
                label={t('admin:settings.booking.refundQuota')}
              />
              <Typography variant="body2" color="text.secondary">
                {t('admin:settings.booking.refundQuotaDescription')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Capacity Settings */}
        <Grid size={{xs:12 , md:6}}>
          <Card>
            <CardHeader
              avatar={<CapacityIcon color="primary" />}
              title={t('admin:settings.capacity.title')}
              subheader={t('admin:settings.capacity.description')}
            />
            <CardContent>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.capacity.trackRealTimeOccupancy}
                    onChange={(e) => handleFormChange('capacity', 'trackRealTimeOccupancy', e.target.checked)}
                  />
                }
                label={t('admin:settings.capacity.trackRealTime')}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t('admin:settings.capacity.trackRealTimeDescription')}
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.capacity.enableCapacityAlerts}
                    onChange={(e) => handleFormChange('capacity', 'enableCapacityAlerts', e.target.checked)}
                  />
                }
                label={t('admin:settings.capacity.enableAlerts')}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('admin:settings.capacity.enableAlertsDescription')}
              </Typography>

              {formData.capacity.enableCapacityAlerts && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Alert Threshold: {Math.round(formData.capacity.capacityAlertThreshold * 100)}%
                  </Typography>
                  <Slider
                    value={formData.capacity.capacityAlertThreshold}
                    onChange={(e, value) => handleFormChange('capacity', 'capacityAlertThreshold', value)}
                    min={0.5}
                    max={1.0}
                    step={0.05}
                    marks={[
                      { value: 0.5, label: '50%' },
                      { value: 0.75, label: '75%' },
                      { value: 0.9, label: '90%' },
                      { value: 1.0, label: '100%' }
                    ]}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Alert when facility reaches {Math.round(formData.capacity.capacityAlertThreshold * 100)}% capacity
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Notification Settings */}
        <Grid size={{xs:12}}>
          <Card>
            <CardHeader
              avatar={<NotificationIcon color="primary" />}
              title={t('admin:settings.notifications.title')}
              subheader={t('admin:settings.notifications.description')}
            />
            <CardContent>
              <Grid container spacing={3}>
                <Grid size={{xs:12 , md:6}}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.notifications.sendCancellationNotifications}
                        onChange={(e) => handleFormChange('notifications', 'sendCancellationNotifications', e.target.checked)}
                      />
                    }
                    label={t('admin:settings.notifications.cancellation')}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {t('admin:settings.notifications.cancellationDescription')}
                  </Typography>
                </Grid>
                
                <Grid size={{xs:12 , md:6}}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.notifications.sendCapacityAlerts}
                        onChange={(e) => handleFormChange('notifications', 'sendCapacityAlerts', e.target.checked)}
                      />
                    }
                    label={t('admin:settings.notifications.capacity')}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {t('admin:settings.notifications.capacityDescription')}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Email Verification Settings */}
        <Grid size={{xs:12}}>
          <Card>
            <CardHeader
              avatar={<EmailIcon color="primary" />}
              title={t('admin:settings.emailVerification.title')}
              subheader={t('admin:settings.emailVerification.description')}
            />
            <CardContent>
              <Grid container spacing={3}>
                <Grid size={{xs:12 , md:6}}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.emailVerification.enableEmailVerification}
                        onChange={(e) => handleFormChange('emailVerification', 'enableEmailVerification', e.target.checked)}
                      />
                    }
                    label={t('admin:settings.emailVerification.enableVerification')}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {t('admin:settings.emailVerification.enableVerificationDescription')}
                  </Typography>
                </Grid>

                <Grid size={{xs:12 , md:6}}>
                  <TextField
                    fullWidth
                    type="number"
                    label={t('admin:settings.emailVerification.expiryHours')}
                    value={formData.emailVerification.expiryHours}
                    onChange={(e) => handleFormChange('emailVerification', 'expiryHours', parseInt(e.target.value) || 24)}
                    inputProps={{ min: 1, max: 168 }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">hours</InputAdornment>
                    }}
                    helperText={t('admin:settings.emailVerification.expiryHoursDescription')}
                  />
                </Grid>

                <Grid size={{xs:12 , md:6}}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.emailVerification.enableRateLimiting}
                        onChange={(e) => handleFormChange('emailVerification', 'enableRateLimiting', e.target.checked)}
                      />
                    }
                    label={t('admin:settings.emailVerification.enableRateLimiting')}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {t('admin:settings.emailVerification.enableRateLimitingDescription')}
                  </Typography>
                </Grid>

                <Grid size={{xs:12 , md:6}}>
                  <TextField
                    fullWidth
                    type="number"
                    label={t('admin:settings.emailVerification.maxRequestsPerHour')}
                    value={formData.emailVerification.maxRequestsPerHour}
                    onChange={(e) => handleFormChange('emailVerification', 'maxRequestsPerHour', parseInt(e.target.value) || 5)}
                    inputProps={{ min: 1, max: 20 }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">requests/hour</InputAdornment>
                    }}
                    helperText={t('admin:settings.emailVerification.maxRequestsPerHourDescription')}
                    disabled={!formData.emailVerification.enableRateLimiting}
                  />
                </Grid>

                <Grid size={{xs:12 , md:6}}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.emailVerification.requireForRegistration}
                        onChange={(e) => handleFormChange('emailVerification', 'requireForRegistration', e.target.checked)}
                      />
                    }
                    label={t('admin:settings.emailVerification.requireForRegistration')}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {t('admin:settings.emailVerification.requireForRegistrationDescription')}
                  </Typography>
                </Grid>

                <Grid size={{xs:12 , md:6}}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.emailVerification.allowAdminOverride}
                        onChange={(e) => handleFormChange('emailVerification', 'allowAdminOverride', e.target.checked)}
                      />
                    }
                    label={t('admin:settings.emailVerification.allowAdminOverride')}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {t('admin:settings.emailVerification.allowAdminOverrideDescription')}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Current Settings Summary */}
        {settings && (
          <Grid size={{xs:12}}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">
                  <InfoIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  {t('admin:settings.summary.title')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid size={{xs:12 , md:4}}>
                    <Typography variant="subtitle2" gutterBottom>{t('admin:settings.summary.bookingRules')}</Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon><TimeIcon fontSize="small" /></ListItemIcon>
                        <ListItemText 
                          primary={t('admin:settings.booking.cancellationDeadline')} 
                          secondary={formatTimeFromHours(settings.booking.cancellationDeadlineHours)} 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          {settings.booking.enableAdminOverride ? <CheckIcon fontSize="small" color="success" /> : <InfoIcon fontSize="small" />}
                        </ListItemIcon>
                        <ListItemText 
                          primary={t('admin:settings.booking.enableAdminOverride')} 
                          secondary={settings.booking.enableAdminOverride ? t('common:enabled') : t('common:disabled')} 
                        />
                      </ListItem>
                    </List>
                  </Grid>
                  <Grid size={{xs:12 , md:4}}>
                    <Typography variant="subtitle2" gutterBottom>{t('admin:settings.summary.capacityManagement')}</Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon>
                          {settings.capacity.trackRealTimeOccupancy ? <CheckIcon fontSize="small" color="success" /> : <InfoIcon fontSize="small" />}
                        </ListItemIcon>
                        <ListItemText 
                          primary={t('admin:settings.capacity.trackRealTime')} 
                          secondary={settings.capacity.trackRealTimeOccupancy ? t('common:active') : t('common:inactive')} 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          {settings.capacity.enableCapacityAlerts ? <CheckIcon fontSize="small" color="success" /> : <InfoIcon fontSize="small" />}
                        </ListItemIcon>
                        <ListItemText 
                          primary={t('admin:settings.capacity.enableAlerts')} 
                          secondary={settings.capacity.enableCapacityAlerts ? `${Math.round(settings.capacity.capacityAlertThreshold * 100)}% ${t('admin:settings.capacity.threshold')}` : t('common:disabled')} 
                        />
                      </ListItem>
                    </List>
                  </Grid>
                  <Grid size={{xs:12 , md:4}}>
                    <Typography variant="subtitle2" gutterBottom>{t('admin:settings.summary.systemInfo')}</Typography>
                    <List dense>
                      <ListItem>
                        <ListItemText 
                          primary={t('admin:settings.summary.lastModified')} 
                          secondary={new Date(settings.lastModified).toLocaleString()} 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary={t('admin:settings.summary.modifiedBy')} 
                          secondary={settings.modifiedBy ? `${settings.modifiedBy.firstName} ${settings.modifiedBy.lastName}` : t('admin:settings.summary.system')} 
                        />
                      </ListItem>
                    </List>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>
        )}

        {/* Action Buttons */}
        <Grid size={{xs:12}}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                color="warning"
                startIcon={<ResetIcon />}
                onClick={handleReset}
                disabled={saving || resetting}
              >
                {resetting ? t('admin:settings.actions.resetting') : t('admin:settings.actions.resetToDefaults')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving || resetting}
              >
                {saving ? t('admin:settings.actions.saving') : t('admin:settings.actions.saveSettings')}
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SystemSettingsPage;